import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { Row, SortingFn } from '@tanstack/react-table';

import { normalizeFrenchSearch } from './normalize-french-search';

/**
 * Shared filter function for range-based column filters (e.g., price, stock quantity).
 * Handles both regular products and virtual parent rows (products with variants).
 *
 * For virtual parent rows, returns true if ANY variant matches the filter range.
 * For regular products, returns true if the product's value matches the filter range.
 *
 * @param row - The table row to filter
 * @param columnId - The column ID (field name) to filter on
 * @param filterValue - The filter value as [min, max] array
 * @returns true if the row should be shown, false otherwise
 */
export function rangeFilterFn(
  row: Row<DealItemWithVersions>,
  columnId: string,
  filterValue: unknown
): boolean {
  // Validate filterValue
  if (
    !Array.isArray(filterValue) ||
    filterValue.length < 2 ||
    typeof filterValue[0] !== 'number' ||
    typeof filterValue[1] !== 'number'
  ) {
    return true; // Show all if filter is invalid
  }

  // filterValue[0] and [1] are already validated as numbers, so we can use them directly
  const filterMin = filterValue[0];
  const filterMax = filterValue[1];

  // Check if this is a virtual parent row (has variants)
  const isVirtualParent = row.subRows.length > 1;

  if (isVirtualParent) {
    // Product has variants: check if ANY variant matches the filter
    const subRows =
      (
        row.original as DealItemWithVersions & {
          _subRows?: DealItemWithVersions[];
        }
      )._subRows || [];

    if (subRows.length === 1) {
      // Edge case: virtual parent with no variants - shouldn't happen but handle it
      return false;
    }
    return subRows.some((variant) => {
      const value = Number(
        variant.activeVersion?.[columnId as keyof DealItem] ?? 0
      );
      return !isNaN(value) && value >= filterMin && value <= filterMax;
    });
  } else {
    // Product without variants: check the product's own value
    const value = Number(
      row.original.activeVersion?.[columnId as keyof DealItem] ?? 0
    );
    return !isNaN(value) && value >= filterMin && value <= filterMax;
  }
}

/**
 * Creates a sorting function that handles grouped parent rows by falling back to subrows.
 * For grouped parent rows where getValue() returns undefined,
 * it falls back to the first subrow's value or the original row's value.
 *
 * @param columnId - The column ID to sort by
 * @param getValueFromRow - Function to extract the value from a row's original data
 * @param locale - Optional locale for locale-aware sorting (defaults to 'fr')
 * @returns A sorting function compatible with TanStack Table
 */
export function createSubRowSortingFn(
  columnId: string,
  getValueFromRow: (row: DealItemWithVersions) => string | null | undefined,
  locale: string = 'fr'
): SortingFn<DealItemWithVersions> {
  return (rowA, rowB) => {
    let a = rowA.getValue(columnId) as string | undefined;
    let b = rowB.getValue(columnId) as string | undefined;

    // For grouped parent rows, getValue returns undefined, so fallback to first subrow or original
    if (a === undefined) {
      const firstSubRowValue = rowA.subRows?.[0]?.original
        ? getValueFromRow(rowA.subRows[0].original)
        : undefined;
      const originalValue = rowA.original
        ? getValueFromRow(rowA.original)
        : undefined;
      a = firstSubRowValue ?? originalValue ?? '';
    }

    if (b === undefined) {
      const firstSubRowValue = rowB.subRows?.[0]?.original
        ? getValueFromRow(rowB.subRows[0].original)
        : undefined;
      const originalValue = rowB.original
        ? getValueFromRow(rowB.original)
        : undefined;
      b = firstSubRowValue ?? originalValue ?? '';
    }

    const aValue = String(a ?? '');
    const bValue = String(b ?? '');

    return aValue.localeCompare(bValue, locale, {
      sensitivity: 'base',
      numeric: true,
    });
  };
}

/**
 * Normalizes a pasted id: trims, lowercases and strips surrounding quotes and
 * zero-width characters (common when copying from DevTools / JSON payloads).
 */
function normalizeIdQuery(value: string): string {
  return value
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .trim()
    .replace(/^["'`]+|["'`,]+$/g, '')
    .trim()
    .toLowerCase();
}

/**
 * Minimum query length for a partial id match. Shorter queries only match an
 * id exactly, so short SKU/GTIN fragments (e.g. "12") don't match every row
 * whose id happens to contain those characters.
 */
export const MIN_PARTIAL_ID_QUERY_LENGTH = 6;

/**
 * True when `query` matches the deal item id or its active version id
 * (case-insensitive, quotes and whitespace ignored): exact match, or substring
 * match once the query has at least MIN_PARTIAL_ID_QUERY_LENGTH characters
 * (unless `exact` is set).
 */
export function matchesDealItemId(
  item: DealItemWithVersions,
  query: string | null | undefined,
  { exact = false }: { exact?: boolean } = {}
): boolean {
  if (!query) return false;
  const normalizedQuery = normalizeIdQuery(query);
  if (!normalizedQuery) return false;
  return [item.id, item.activeVersion?.id].some((id) => {
    if (!id) return false;
    const normalizedId = id.toLowerCase();
    return !exact && normalizedQuery.length >= MIN_PARTIAL_ID_QUERY_LENGTH
      ? normalizedId.includes(normalizedQuery)
      : normalizedId === normalizedQuery;
  });
}

/** Prefix of deal item ids (e.g. "di_01a0a582408572c199b0a36e9796e24e"). */
const DEAL_ITEM_ID_PREFIX = 'di_';

/**
 * True when a search should show `item` whatever the display mode (e.g. a
 * deselected item while on "Included"): the query is exactly one of its ids,
 * or is unambiguously a deal item id (starts with "di_") and partially matches.
 * Other partial matches don't bypass the display mode, so ordinary SKU/GTIN
 * searches that happen to appear inside an id don't surface hidden rows.
 */
export function shouldBypassDisplayModeForIdSearch(
  item: DealItemWithVersions,
  query: string | null | undefined
): boolean {
  if (!query) return false;
  if (matchesDealItemId(item, query, { exact: true })) return true;
  return (
    normalizeIdQuery(query).startsWith(DEAL_ITEM_ID_PREFIX) &&
    matchesDealItemId(item, query)
  );
}

/**
 * Filter function for the table search bar.
 *
 * Matches (accent/case-insensitive substring) on name, SKU, GTIN and Shopify
 * variant id, and on the deal item id or its active version id (see
 * matchesDealItemId).
 */
export function searchTermsFilterFn(
  row: Row<DealItemWithVersions>,
  _columnId: string,
  filterValue: string
): boolean {
  if (matchesDealItemId(row.original, filterValue)) return true;

  const normalizedFilterValue = normalizeFrenchSearch(filterValue);
  const matchesFilterValue = (value: string | null | undefined) =>
    value !== null &&
    value !== undefined &&
    normalizeFrenchSearch(value).includes(normalizedFilterValue);

  const { activeVersion } = row.original;

  return (
    matchesFilterValue(activeVersion.name) ||
    matchesFilterValue(activeVersion.sku) ||
    matchesFilterValue(activeVersion.gtin) ||
    matchesFilterValue(activeVersion.shopifyVariantId)
  );
}
